import {
  Flex,
  Skeleton,
  Card,
  CardBody,
  Box,
  Button,
  Spacer,
} from '@chakra-ui/react';
import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { date as commonDate } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { LimitedPartnerPortfolioOverviewGraphDataGQLType } from '../../../../types/dashboard-admin-gqlTypes.generated.d';
import { useGetLimitedPartnerPortfolioOverviewGraphDataPgQuery } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { PortfolioTimePeriodSelectButtons } from '../../portfolio/ui/shared/PortfolioTimePeriodSelectButtons';
import { Icon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';
import { colors } from '~/packages/common-frontend/colors';
import { getDefaultDashboardDisplayEndDate } from '~/packages/common-frontend/constants';
import { PortfolioChartCards } from '~/packages/portfolio/ui/shared/PortfolioChartCards';
import {
  ChartTimePeriod,
  PortfolioChartConfig,
  formatChartDataByReportingDate,
  getInitialActiveFields,
  onToggleActiveField,
} from '~/packages/portfolio/ui/shared/portfolioChartUtils';
import { PortfolioReChart } from '~/packages/portfolio/ui/shared/PortfolioReChart';

type PortfolioOverviewGraphData = {
  reportingDate: Util.FullDateStr;
  totalPrincipalBalance: number;
  totalUncalledCapital: number;
  totalCommittedCapital: number;
};

export const portfolioOverviewChartConfig: PortfolioChartConfig<
  keyof PortfolioOverviewGraphData
> = {
  totalCommittedCapital: {
    chartType: 'line',
    field: 'totalCommittedCapital',
    label: 'Committed Capital',
    format: 'currency',
    color: colors.chart.yellow,
  },
  totalUncalledCapital: {
    chartType: 'line',
    field: 'totalUncalledCapital',
    label: 'Uncalled Capital',
    format: 'currency',
    color: colors.blue[200],
  },
  totalPrincipalBalance: {
    chartType: 'bar',
    field: 'totalPrincipalBalance',
    label: 'Principal Balance',
    format: 'currency',
    color: colors.indigo[200],
    activeColor: colors.indigo[400],
  },
};

const CustomLegend = () => {
  return (
    <Flex mb="2rem" gap="1rem">
      <Flex alignItems="center" gap="0.5rem">
        <Box
          backgroundColor={colors.chart.yellow}
          borderRadius="4px"
          h="8px"
          w="8px"
        />
        <Typography variant="detail-muted">Committed Capital</Typography>
      </Flex>
      <Flex alignItems="center" gap="0.5rem">
        <Box
          backgroundColor={colors.blue[200]}
          borderRadius="4px"
          h="8px"
          w="8px"
        />
        <Typography variant="detail-muted">Uncalled Capital</Typography>
      </Flex>
      <Flex alignItems="center" gap="0.5rem">
        <Box
          backgroundColor={colors.indigo[400]}
          borderRadius="4px"
          h="8px"
          w="8px"
        />
        <Typography variant="detail-muted">Principal Balance</Typography>
      </Flex>
    </Flex>
  );
};

export const LimitedPartnersPortfolioOverviewGraphContainer = ({
  lenderOrganizationId,
}: {
  lenderOrganizationId: string;
}) => {
  const {
    data: queryData,
    error: portfolioOverviewGraphDataError,
  } = useGetLimitedPartnerPortfolioOverviewGraphDataPgQuery({
    variables: {
      lenderOrganizationId,
    },
  });
  if (portfolioOverviewGraphDataError) {
    throw portfolioOverviewGraphDataError;
  }

  const initialActiveFields = getInitialActiveFields({
    chartConfig: portfolioOverviewChartConfig,
  }) as (keyof PortfolioOverviewGraphData)[];

  const [activeFields, setActiveFields] = useState<
    (keyof PortfolioOverviewGraphData)[]
  >(initialActiveFields);

  const [selectedBar, setSelectedBar] = useState<PortfolioOverviewGraphData>();

  // need both initial date range chart data (returned by query)
  // as well as chart data for time period
  // because we only want to filter chart data for time period on top of initial date range chart data
  // and not include a previously-filtered time period
  const [chartDataForDateRange, setChartDataForDateRange] = useState<
    LimitedPartnerPortfolioOverviewGraphDataGQLType[]
  >([]);
  const [chartDataWithTimePeriod, setChartDataWithTimePeriod] = useState<
    LimitedPartnerPortfolioOverviewGraphDataGQLType[]
  >([]);

  const [selectedTimePeriod, setSelectedTimePeriod] = useState<ChartTimePeriod>(
    ChartTimePeriod.month,
  );

  useEffect(() => {
    if (queryData?.portfolioOverviewGraphData) {
      const formattedChartData = formatChartDataByReportingDate({
        chartData: queryData?.portfolioOverviewGraphData,
        selectedTimePeriod: ChartTimePeriod.day,
      });
      setChartDataForDateRange(formattedChartData);

      // then set data for the specific time period
      if (selectedTimePeriod !== ChartTimePeriod.day) {
        const formattedChartDataForPeriod = formatChartDataByReportingDate({
          chartData: queryData?.portfolioOverviewGraphData,
          selectedTimePeriod,
        });
        setChartDataWithTimePeriod(formattedChartDataForPeriod);
      } else {
        setChartDataWithTimePeriod(formattedChartData);
      }

      setSelectedBar(formattedChartData[formattedChartData.length - 1]);
    }
  }, [queryData?.portfolioOverviewGraphData, selectedTimePeriod]);

  const onSelectTimePeriod = (timePeriod: ChartTimePeriod) => {
    setSelectedTimePeriod(timePeriod);
    const formattedChartData = formatChartDataByReportingDate({
      chartData: chartDataForDateRange,
      selectedTimePeriod: timePeriod,
    });

    setChartDataWithTimePeriod(formattedChartData);
  };

  // @makarand-todo: enable selecting the date range.
  // also enable FullDateRangePicker from below.
  // const [_chartDataDateRange, _setChartDataDateRange] = useState<{
  //   chartDataStartDate: Date;
  //   chartDataEndDate: Date;
  // }>({
  //   chartDataStartDate: chartsInitialStartDateJs,
  //   chartDataEndDate: chartsInitialEndDateJs,
  // });

  if (!queryData?.portfolioOverviewGraphData) {
    return (
      <Box m="2.5rem 0rem 4rem">
        <Skeleton height="60vh" />
      </Box>
    );
  }

  return (
    <Box m="2.5rem 0rem 4rem">
      <>
        <Card
          variant="outline"
          size="lg"
          borderBottomLeftRadius="0px"
          borderBottomRightRadius="0px"
        >
          <CardBody display="flex" flexDirection="column" gap="1.5rem">
            <Typography trim fontWeight="semibold">
              Portfolio Overview
            </Typography>
            <Flex>
              {/* <FullDateRangePicker
                  onSelectDateRange={() => {}}
                  currentDateRange={{
                    startDate: chartDataDateRange.chartDataStartDate,
                    endDate: chartDataDateRange.chartDataEndDate,
                  }}
                  w="lg"
                /> */}
              <Spacer />
              <PortfolioTimePeriodSelectButtons
                currentSelectedTimePeriod={selectedTimePeriod}
                onSelectTimePeriod={onSelectTimePeriod}
              />
            </Flex>

            <Flex>
              <Box width="70%">
                <PortfolioReChart
                  chartData={chartDataWithTimePeriod}
                  selectedTimePeriod={selectedTimePeriod}
                  currentSelectedBarReportingDate={
                    selectedBar
                      ? selectedBar.reportingDate
                      : getDefaultDashboardDisplayEndDate()
                  }
                  onSelectBar={setSelectedBar}
                  chartConfig={portfolioOverviewChartConfig}
                  visibleChartComponents={activeFields}
                  chartMargin={{ top: 0, right: 20, left: 10, bottom: 0 }}
                  legendAlign={{
                    horizontalAlign: 'left',
                    verticalAlign: 'top',
                  }}
                  renderCustomLegend={CustomLegend}
                  legendWrapperStyle={{}}
                  tooltipLabelType="monthYear"
                />
              </Box>
              <Spacer />
              {selectedBar && (
                <Flex width="25%" flexDirection="column">
                  <PortfolioChartCards
                    selectedBarData={selectedBar}
                    chartConfig={portfolioOverviewChartConfig}
                    activeFields={activeFields}
                    onToggleActiveField={(selectedField) =>
                      onToggleActiveField({
                        selectedField,
                        currentActiveFields: activeFields,
                        setActiveFields,
                      })
                    }
                    customTitleFormatter={(selectedBarData) =>
                      `${commonDate.fullDateStrToStr(
                        selectedBarData.reportingDate,
                        'LLLL',
                      )} ${commonDate.fullDateStrToStr(
                        selectedBarData.reportingDate,
                        'yyyy',
                      )}`
                    }
                  />
                </Flex>
              )}
            </Flex>
          </CardBody>
        </Card>
        <Card
          variant="outline"
          size="lg"
          borderTopLeftRadius="0px"
          borderTopRightRadius="0px"
          borderTop="none"
        >
          <CardBody>
            <Flex justifyContent="flex-end">
              <Button
                colorScheme="gray"
                as={RouterLink}
                to="/limited-partners/insights"
                rightIcon={<Icon icon="chevron-right" />}
              >
                View Insights
              </Button>
            </Flex>
          </CardBody>
        </Card>
      </>
    </Box>
  );
};
